import { LoadingBoundary } from '@app/pages/i/components/LoadingBoundary';
import Box from '@redbubble/design-system/src/Box';
import Text from '@redbubble/design-system/src/Text';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import Image from 'next/image';
import type { SearchResultsPageProps } from '../../types';
import {
  FallbackHeaderLading,
  HeroImageHeaderLoading,
  HeroTextHeaderLoading,
} from './loading';
import styles from './SearchHeader.module.scss';

export interface SearchHeaderProps {
  heroData: SearchResultsPageProps['heroData'];
  title: SearchResultsPageProps['title'];
}

interface HeroProps {
  hero: NonNullable<SearchResultsPageProps['heroData']>;
}

const messages = defineMessages({
  shopTitle: { defaultMessage: 'Shop' },
  resultsCount: { defaultMessage: '{ resultCount, number } Results' },
});

const FallbackHeader: React.FC<Pick<SearchHeaderProps, 'title'>> = ({
  title,
}) => {
  const intl = useIntl();
  const searchGridTitle = title || intl.formatMessage(messages.shopTitle);

  return (
    <Box className={styles.outerContainer} aria-level="1">
      <Box className={styles.titleContainer}>
        <h1 className={styles.title} aria-live="polite">
          {searchGridTitle}
        </h1>
      </Box>
    </Box>
  );
};

const HeroImageHeader: React.FC<HeroProps> = ({ hero }) => {
  const heroVariables = {
    '--image-background-color': hero.color,
  };

  const imageSrc =
    hero.image && hero.image.startsWith('//')
      ? `https:${hero.image}`
      : hero.image;

  return (
    <Box
      style={heroVariables}
      className={styles.imageHeaderContainer}
      aria-level="1"
    >
      <Box className={styles.titleContainer}>
        <h1 className={styles.title} aria-live="polite">
          {hero.title}
        </h1>
        {hero.pitch && (
          <p
            className={styles.pitch}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: noDangerouslySetInnerHtml
            dangerouslySetInnerHTML={{ __html: hero.pitch }}
          />
        )}
      </Box>
      <Box className={styles.heroImage}>
        {imageSrc && (
          <Image
            src={imageSrc}
            className={styles.image}
            width={600}
            height={600}
            alt={`An image describing ${hero.title}`}
            // TODO: Remove after react upgrade, check tests pass
            fetchPriority="high"
            priority
          />
        )}
      </Box>
    </Box>
  );
};

const HeroTextHeader: React.FC<HeroProps> = ({ hero }) => {
  return (
    <Box className={styles.outerContainer} aria-level="1">
      <Box className={styles.titleContainer}>
        <h1 className={styles.title} aria-live="polite">
          {hero.title}
        </h1>
        {hero.pitch && (
          <Box className={styles.pitch}>
            <Text
              display="block"
              type="body2"
              // biome-ignore lint/security/noDangerouslySetInnerHtml: noDangerouslySetInnerHtml
              dangerouslySetInnerHTML={{
                __html: hero.pitch,
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const SearchHeader: React.FC<SearchHeaderProps> = ({
  heroData: hero,
  title,
}) => {
  if (hero) {
    if (hero.color && hero.image) {
      return (
        <LoadingBoundary loadingComponent={<HeroImageHeaderLoading />}>
          <HeroImageHeader hero={hero} />
        </LoadingBoundary>
      );
    }
    if (hero.title) {
      return (
        <LoadingBoundary loadingComponent={<HeroTextHeaderLoading />}>
          <HeroTextHeader hero={hero} />
        </LoadingBoundary>
      );
    }
  }

  return (
    <LoadingBoundary loadingComponent={<FallbackHeaderLading />}>
      <FallbackHeader title={title} />
    </LoadingBoundary>
  );
};
